import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { Destination } from '../models/destination.model';
import { TestDetails } from '../models/test-result.model';
import { VoiceTestGroup, VoiceTestResult, VoiceTestResultCollection, VoiceTestStatus } from '../models/voice-testing.model';
import { RestUtils } from './rest-utils';
declare const moment: any;

@Injectable()
export class VoiceTestingService {
  private utils = new RestUtils();
  private headers = new HttpHeaders();

  private actionSubject = new Subject<VtActionData>();
  action$ = this.actionSubject.asObservable();

  constructor(private http: HttpClient) {
    this.headers = this.headers.set('Content-Type', 'application/json');
  }

  announceAction(action: VtActionData) {
    this.actionSubject.next(action);
  }

  results(params: AllRequestParams, search?: SearchParams) {
    const queryParams = {
      page: params.page,
      size: params.size,
      sort: ['id,desc'],
    };

    const url = this.utils.buildUrl('ROLE/voice/test-results/search', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<VoiceTestResultCollection>(url, search, options);
  }

  run(group: VoiceTestGroup) {
    const url = this.utils.buildUrl(`ROLE/voice/test-groups`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, group, options);
  }

  getResultDetailsText(result: VoiceTestResult): Observable<TestDetails[]> {
    const text = [];
    text.push('ID: ' + result.id);
    text.push('Test group ID: ' + result.testGroupId);
    text.push('Date: ' + moment(result.createdAt).format('DD MMM YYYY HH:mm:ss'));
    text.push('Country: ' + this.formatDestination(result.destination));
    text.push('Phone: ' + (result.destination.phone ? result.destination.phone : 'N/A'));
    text.push('Rec. Status: ' + result.testStatus);
    text.push('Delay: ' + (result.delay ? result.delay + 's' : 'N/A'));
    text.push('Caller: ' + (result.callerId ? result.callerId : 'N/A'));
    text.push('Call created: ' + (result.callCreatedAt ? moment(result.callCreatedAt).format('DD MMM YYYY HH:mm:ss') : 'N/A'));
    text.push('Call connected: ' + (result.callConnectedAt ? moment(result.callConnectedAt).format('DD MMM YYYY HH:mm:ss') : 'N/A'));
    return of([
      {
        id: 'general',
        title: 'General',
        data: text,
      },
    ]);
  }

  getSipStatuses(id: number): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/voice/sip-status/${id}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<any>(url, options);
  }

  formatDestination(dest: Destination, icon = false): string {
    if (dest.mnc === null && dest.mcc === null) {
      return (dest.countryName ? dest.countryName : 'Unknown') + '/' + dest.phone;
    }

    let str =
      (dest.countryName ? dest.countryName : 'Unknown') +
      '(' +
      dest.mcc +
      ')/' +
      (dest.providerName ? dest.providerName : 'Unknown') +
      '(' +
      dest.mnc +
      ')';

    if (dest.originalMnc) {
      const iconStr = icon ? '<i class="icon-ported"></i>' : '<=';
      const provider = dest.originalProviderName ? dest.originalProviderName : 'Unknown';
      let originalStr = ' ' + iconStr + ' ' + provider + '(' + dest.originalMnc + ')';
      if (icon) {
        originalStr = `<span title="Ported from ${provider}">${originalStr}</span>`;
      }
      str += originalStr;
    }

    return str;
  }

  recentDestinations() {
    const url = this.utils.buildUrl('ROLE/voice/recent-destinations', {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<Destination[]>(url, options);
  }

  reimburse(testIds: number[]): Observable<void> {
    const url = this.utils.buildUrl(`ROLE/voice/tests/release-credits`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<void>(url, testIds, options);
  }
}

export class AllRequestParams {
  size = 20;
  page = 1;
  search: string;

  sort: string[] = [];

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}

export interface SearchParams {
  testGroupIds?: number[];
  testIds?: number[];
  userIds?: number[];
  networkDetails?: Destination[];
  callerIds?: string[];
  sipCallerIds?: string[];
  createdAtFrom?: string;
  createdAtTo?: string;
  statuses?: VoiceTestStatus[];
  sipConfigIds?: number[];
  sipStatusCodes?: number[];
  sip: boolean;
}

interface VtActionData {
  name: string;
  row: VoiceTestResult;
  column: string;
}
